import { X, LogOut, Star, TrendingUp, BarChart3, Calculator, CalendarDays, PieChart, Wallet, Target, Share2, FileSpreadsheet, Briefcase } from 'lucide-react';

// Mirrors the FEATURES list in LoginModal so the logged-in user sees
// the same value-prop he/she signed up to access.
const FEATURES = [
  { icons: [Star, Briefcase], text: 'Watchlist / Portfolio — track holdings & gains' },
  { icon: TrendingUp, text: 'Performance — see your return over time' },
  { icon: Target, text: 'Monthly Income Goal — set a target & track progress' },
  { icon: BarChart3, text: 'Yield History — spot dividend trends & risks' },
  { icon: Calculator, text: 'DRIP Calculator — model dividend reinvestment' },
  { icon: CalendarDays, text: 'Dividend Calendar — upcoming ex-div & pay dates' },
  { icon: PieChart, text: 'Sector Rebalancer — plan buys & sells by target %' },
  { icon: Wallet, text: 'Cash Tracking — deposits, sells & full transaction history' },
  { icon: FileSpreadsheet, text: 'Google / Excel Sheet — downloadable dividend tracker pre-filled with your portfolio' },
  { icon: Share2, text: 'My Share Page — public read-only snapshot link' },
];

export default function LogoutModal({ open, onClose, onConfirm }) {
  if (!open) return null;

  // Intentionally NO outside-click close. User must pick No or Yes.
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="logout-modal-overlay"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-md relative max-h-[90vh] overflow-y-auto pb-24 sm:pb-4"
        data-testid="logout-modal"
      >
        {/* Red X close (counts as No) */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-colors z-10"
          data-testid="logout-modal-close"
          aria-label="Cancel — keep me logged in"
        >
          <X className="w-4 h-4" strokeWidth={3} />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="flex items-start gap-3 mb-4 pr-8">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center shrink-0">
              <LogOut className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Are you sure you want to log out?</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                By logging out you lose access to:
              </p>
            </div>
          </div>

          {/* Feature list — kept in sync with LoginModal */}
          <div className="space-y-1.5 mb-5">
            {FEATURES.map((f, i) => {
              const icons = f.icons || [f.icon];
              return (
                <div key={i} className="flex items-center gap-2.5 text-[12px] leading-tight">
                  <span className="inline-flex items-center gap-1 shrink-0">
                    {icons.map((Icon, j) => (
                      <Icon key={j} className="w-4 h-4 text-primary" />
                    ))}
                  </span>
                  <span>{f.text}</span>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-muted-foreground mb-4">
            Browsing dividend info remains free — you can always log back in later to pick up where you left off.
          </p>

          {/* Action buttons */}
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-border bg-card text-sm font-semibold hover:bg-muted transition-colors"
              data-testid="logout-modal-no"
            >
              No
            </button>
            <button
              onClick={onConfirm}
              className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold shadow-sm transition-colors"
              data-testid="logout-modal-yes"
            >
              Yes, Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
